import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL = 'https://api.innoworx.ai/Stage';

// Opt-in mock mode for local UI development if needed.
const USE_MOCK_REFERRALS = process.env.REACT_APP_USE_MOCK_REFERRALS === 'true';
const MOCK_REFERRALS = [];

const warnMocksDisabledOnce = (() => {
  let didWarn = false;
  return () => {
    if (didWarn) return;
    didWarn = true;
    // eslint-disable-next-line no-console
    // console.warn(
    //   '[HyperLocalReferralTracker] Using live Restaurant Ops Suite APIs. Set REACT_APP_USE_MOCK_REFERRALS=true to use demo data.'
    // );
  };
})();

const maybeDelay = async (ms) => {
  if (!USE_MOCK_REFERRALS) return;
  await new Promise((r) => setTimeout(r, ms));
};

const extractList = (payload, preferredKeys = []) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];
  for (const key of preferredKeys) {
    if (Array.isArray(payload[key])) return payload[key];
  }
  return [];
};

const getAuthToken = async () => {
  try {
    const session = await fetchAuthSession();
    const token = session?.tokens?.idToken?.toString();
    return token?.trim() || null;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[HyperLocalReferralTracker] Failed to fetch auth session', err);
    return null;
  }
};

const buildQueryString = (query) => {
  if (!query) return '';
  const params = Object.entries(query).reduce((acc, [key, val]) => {
    if (val === undefined || val === null || val === '') return acc;
    acc.append(key, val);
    return acc;
  }, new URLSearchParams());
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

const marketingRequest = async (method, path, { body, query } = {}) => {
  if (USE_MOCK_REFERRALS) {
    throw new Error('Mock mode enabled; live API calls are disabled.');
  }

  const token = await getAuthToken();
  if (!token) {
    const authError = new Error('Missing authentication token. Please sign in again.');
    authError.code = 'AUTHENTICATION_FAILED';
    
    // Log authentication error to logging service
    loggingService.logError(
      'api_error_hyperlocal_referral',
      `Hyper Local Referral API authentication error for ${method} ${path}`,
      authError,
      {
        endpoint: path,
        method,
        errorType: 'AuthenticationError',
      }
    ).catch(logError => {
      console.warn('[referralTrackerApi] Failed to log error:', logError);
    });
    
    throw authError;
  }

  const url = `${API_BASE_URL}${path}${buildQueryString(query)}`;
  
  let response;
  try {
    response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (fetchError) {
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_hyperlocal_referral',
      `Hyper Local Referral API network/fetch error for ${method} ${path}`,
      fetchError,
      {
        endpoint: path,
        method,
        errorType: fetchError?.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[referralTrackerApi] Failed to log error:', logError);
    });
    
    throw fetchError;
  }

  if (!response.ok) {
    const errText = await response.text().catch(() => '');
    let message = response.statusText || 'Unknown error';
    try {
      const errJson = errText ? JSON.parse(errText) : null;
      if (errJson && typeof errJson.error === 'string') message = errJson.error;
      else if (errText) message = errText;
    } catch {
      if (errText) message = errText;
    }
    
    const error = new Error(`Request failed ${method} ${path}: ${response.status} ${message}`);
    error.status = response.status;
    error.statusText = response.statusText;
    error.endpoint = path;
    error.responseData = errText;
    
    // Log API error to logging service
    loggingService.logError(
      'api_error_hyperlocal_referral',
      `Hyper Local Referral API error: ${response.status} ${response.statusText} for ${method} ${path}`,
      error,
      {
        endpoint: path,
        method,
        status: response.status,
        statusText: response.statusText,
        responseData: errText,
      }
    ).catch(logError => {
      console.warn('[referralTrackerApi] Failed to log error:', logError);
    });
    
    throw error;
  }

  // Log API event for successful request
  loggingService.logInfo(
    'api_success_hyperlocal_referral',
    `Successfully completed ${method} request to ${path}`,
    {
      endpoint: path,
      method,
      status: response.status,
    }
  ).catch(logError => {
    console.warn('[referralTrackerApi] Failed to log success event:', logError);
  });

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }
  return response.text();
};

// Simple in-memory caches to avoid double-fetching when both list + stats are requested together.
let referralCache = null;
let referralCacheTimestamp = 0;
let referralCachePromise = null;
const CACHE_TTL_MS = 60 * 1000; // 1 minute is enough to coalesce concurrent requests.

/**
 * GET /marketing/influencers?site_id={site_id}&status={status}
 * @param {Object} [query] - Optional filters
 * @param {string} [query.site_id] - Filter by site
 * @param {string} [query.status] - Filter by status (active|inactive|archived)
 */
const fetchInfluencers = async (query = {}) => {
  const res = await marketingRequest('GET', '/marketing/influencers', { query });
  return extractList(res, ['influencers']);
};

/**
 * GET /marketing/referral-codes
 * Lists referral codes for the tenant, optionally filtered by site and/or influencer.
 * @param {Object} [query] - Optional filters
 * @param {string} [query.site_id] - Filter by site
 * @param {string} [query.influencer_id] - Filter by influencer
 * @returns {Promise<Array<Object>>} Array of referral code records (referral_code, influencer_id, tenant_id, site_id, qr_payload, status, created_at, updated_at). Empty array if no matches.
 * @throws {Error} 401 no tenant, 500 DynamoDB error
 */
export const fetchReferralCodes = async (query = {}) => {
  const res = await marketingRequest('GET', '/marketing/referral-codes', { query });
  return extractList(res, ['referral_codes', 'referralCodes', 'codes']);
};

/**
 * GET /marketing/referral-codes/{code}
 * Fetches a single referral code by code (path param). Enforces tenant.
 * @param {string} code - Referral code from path (e.g. CODE123)
 * @returns {Promise<Object>} The referral code item (referral_code, influencer_id, tenant_id, site_id, qr_payload, status, created_at, updated_at)
 * @throws {Error} 400 missing param, 401 no tenant, 404 not found, 500 server error
 */
export const getReferralCodeByCode = async (code) => {
  const trimmed = typeof code === 'string' ? code.trim() : '';
  if (!trimmed) {
    throw new Error('Referral code is required');
  }
  const path = `/marketing/referral-codes/${encodeURIComponent(trimmed)}`;
  try {
    return await marketingRequest('GET', path);
  } catch (error) {
    // Check if it's a 404 error (referral code not found)
    if (error?.message && (error.message.includes('404') || error.message.includes('not found'))) {
      const notFoundError = new Error('Referral code not found');
      notFoundError.status = 404;
      notFoundError.isNotFound = true;
      throw notFoundError;
    }
    throw error;
  }
};

/**
 * Create a referral code for an influencer.
 * POST /marketing/referral-codes
 * @param {Object} payload
 * @param {string} payload.influencer_id - Required. Influencer that owns this referral code.
 * @param {string} [payload.referral_code] - Code value. If omitted, backend generates 8-char uppercase.
 * @param {string} [payload.site_id] - Site to associate with the code.
 * @param {string} [payload.qr_payload] - Payload for QR (defaults to referral_code if not sent).
 * @param {string} [payload.status] - e.g. "active". Defaults to "active".
 * @returns {Promise<{ message: string, referral_code: string }>} 201 with message and referral_code
 * @throws {Error} 400 if influencer_id missing, 401 no tenant, 500 server error
 */
export const createReferralCode = async (payload) => {
  if (USE_MOCK_REFERRALS) {
    throw new Error('Mock mode enabled; live API calls are disabled.');
  }
  const influencerId =
    payload != null && payload.influencer_id != null ? String(payload.influencer_id).trim() : '';
  if (!influencerId) {
    throw new Error('influencer_id is required');
  }
  const body = { influencer_id: influencerId };
  if (payload?.referral_code != null && String(payload.referral_code).trim() !== '') {
    body.referral_code = String(payload.referral_code).trim();
  }
  if (payload?.site_id != null && String(payload.site_id).trim() !== '') {
    body.site_id = String(payload.site_id).trim();
  }
  if (payload?.qr_payload != null && String(payload.qr_payload).trim() !== '') {
    body.qr_payload = String(payload.qr_payload).trim();
  }
  if (payload?.status != null && String(payload.status).trim() !== '') {
    body.status = String(payload.status).trim();
  }
  const res = await marketingRequest('POST', '/marketing/referral-codes', { body });
  referralCache = null;
  referralCacheTimestamp = 0;
  return res;
};

const attributionCache = new Map();
const fetchAttributionsForCode = async (referralCode, influencerId = null) => {
  if (!referralCode) return [];
  
  // Create cache key that includes both referral_code and influencer_id if provided
  const cacheKey = influencerId ? `${referralCode}:${influencerId}` : referralCode;
  if (attributionCache.has(cacheKey)) return attributionCache.get(cacheKey);
  
  const query = { referral_code: referralCode };
  if (influencerId) {
    query.influencer_id = influencerId;
  }
  
  const res = await marketingRequest('GET', '/marketing/attributions', { query });
  const records = extractList(res, ['attributions']);
  
  // Filter attributions by influencer_id if provided (in case API doesn't filter)
  let filteredRecords = records;
  if (influencerId && records.length > 0) {
    filteredRecords = records.filter(attr => {
      const attrInfluencerId = attr.influencer_id || attr.influencerId;
      return attrInfluencerId === influencerId;
    });
  }
  
  attributionCache.set(cacheKey, filteredRecords);
  return filteredRecords;
};

/**
 * Apply referral code (Create attribution)
 * POST /marketing/attributions
 * Records when a customer uses a referral code (e.g., at checkout)
 * @param {Object} payload
 * @param {string} payload.order_id - Order ID (required)
 * @param {string} payload.influencer_id - Influencer ID (required)
 * @param {string} payload.referral_code_applied - Referral code that was applied (required)
 * @param {string} [payload.customer_id] - Customer ID
 * @param {string} [payload.customer_name] - Customer name
 * @param {string} [payload.applied_by] - Name of person applying the code
 * @param {number} [payload.order_value] - Order value/amount
 * @param {string} [payload.site_id] - Site ID
 * @returns {Promise<Object>} Response with success status and attribution data
 * @throws {Error} 400 if required fields missing, 401 no tenant, 500 server error
 */
export const createAttribution = async (payload) => {
  if (USE_MOCK_REFERRALS) {
    throw new Error('Mock mode enabled; live API calls are disabled.');
  }

  // Validate required fields
  const orderId = payload?.order_id ? String(payload.order_id).trim() : '';
  const influencerId = payload?.influencer_id ? String(payload.influencer_id).trim() : '';
  const referralCode = payload?.referral_code_applied ? String(payload.referral_code_applied).trim() : '';

  if (!orderId) {
    throw new Error('order_id is required');
  }
  if (!influencerId) {
    throw new Error('influencer_id is required');
  }
  if (!referralCode) {
    throw new Error('referral_code_applied is required');
  }

  const body = {
    order_id: orderId,
    influencer_id: influencerId,
    referral_code_applied: referralCode,
  };

  // Add optional fields if provided
  if (payload?.customer_id) {
    body.customer_id = String(payload.customer_id).trim();
  }
  if (payload?.customer_name) {
    body.customer_name = String(payload.customer_name).trim();
  }
  if (payload?.applied_by) {
    body.applied_by = String(payload.applied_by).trim();
  }
  if (payload?.order_value != null) {
    body.order_value = Number(payload.order_value);
  }
  if (payload?.site_id) {
    body.site_id = String(payload.site_id).trim();
  }

  const res = await marketingRequest('POST', '/marketing/attributions', { body });
  
  // Clear attribution cache for this referral code and influencer combination
  // Clear both the specific cache key and the general referral code cache
  const specificCacheKey = `${referralCode}:${influencerId}`;
  attributionCache.delete(specificCacheKey);
  attributionCache.delete(referralCode);
  
  // Also clear the referral cache so the list refreshes with updated counts
  referralCache = null;
  referralCacheTimestamp = 0;
  
  return res;
};

const findLatestTimestamp = (records, keys) => {
  let latest = null;
  records.forEach((record) => {
    keys.forEach((k) => {
      if (record?.[k]) {
        const ts = Date.parse(record[k]);
        if (!Number.isNaN(ts) && (!latest || ts > latest)) {
          latest = ts;
        }
      }
    });
  });
  return latest ? new Date(latest).toISOString() : null;
};

const buildInfluencerView = (influencer, codes = [], attributions = []) => {
  const primaryCode = codes[0];
  const referralCode = primaryCode?.referral_code || primaryCode?.code;
  const referralCount = attributions.length;
  const lastReferralAt = findLatestTimestamp(attributions, [
    'referred_at',
    'created_at',
    'updated_at',
  ]);

  return {
    influencer_id: influencer.influencer_id || influencer.id,
    name: influencer.name,
    email: influencer.email,
    phone: influencer.phone,
    referral_code: referralCode,
    status: influencer.status || 'active',
    site_id: influencer.site_id || primaryCode?.site_id,
    site_name: influencer.site_name || primaryCode?.site_name,
    location: influencer.location || influencer.city,
    region: influencer.region,
    referral_count: referralCount,
    created_at: influencer.created_at,
    updated_at: influencer.updated_at,
    last_referral_at: lastReferralAt,
  };
};

const fetchAndAssembleReferrals = async () => {
  if (USE_MOCK_REFERRALS) {
    await maybeDelay(300);
    return [...MOCK_REFERRALS];
  }

  const [influencers, codes] = await Promise.all([fetchInfluencers(), fetchReferralCodes()]);
  const codesByInfluencer = codes.reduce((map, code) => {
    const key = code.influencer_id || code.influencerId;
    if (!key) return map;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(code);
    return map;
  }, new Map());

  const results = [];
  for (const influencer of influencers) {
    const infId = influencer.influencer_id || influencer.id;
    const infCodes = codesByInfluencer.get(infId) || [];
    // Pass influencer_id to filter attributions correctly
    const attributions = (
      await Promise.all(
        infCodes.map((code) => fetchAttributionsForCode(code.referral_code || code.code, infId))
      )
    ).flat();
    results.push(buildInfluencerView(influencer, infCodes, attributions));
  }

  return results;
};

const getReferralsWithCache = async () => {
  if (
    referralCache &&
    Date.now() - referralCacheTimestamp < CACHE_TTL_MS &&
    !USE_MOCK_REFERRALS
  ) {
    // eslint-disable-next-line no-console
    console.log('[HyperLocalReferralTracker] Using cached referrals', {
      count: referralCache.length,
      cachedAt: new Date(referralCacheTimestamp).toISOString(),
    });
    return referralCache;
  }

  if (referralCachePromise) {
    return referralCachePromise;
  }

  referralCachePromise = fetchAndAssembleReferrals()
    .then((data) => {
      // eslint-disable-next-line no-console
      console.log('[HyperLocalReferralTracker] Fetched referrals from API', {
        count: Array.isArray(data) ? data.length : 0,
      });
      referralCache = data;
      referralCacheTimestamp = Date.now();
      referralCachePromise = null;
      return data;
    })
    .catch((err) => {
      referralCachePromise = null;
      throw err;
    });

  return referralCachePromise;
};

/**
 * Fetch all influencer referrals for the tenant.
 * @returns {Promise<Array>} List of referral/influencer records
 */
export const getAllReferrals = async () => {
  // if (USE_MOCK_REFERRALS) {
  //   warnMocksDisabledOnce();
  //   // eslint-disable-next-line no-console
  //   console.log('[HyperLocalReferralTracker] Returning mock referrals', {
  //     count: MOCK_REFERRALS.length,
  //   });
  //   return [...MOCK_REFERRALS];
  // }
  warnMocksDisabledOnce();
  const data = await getReferralsWithCache();
  // eslint-disable-next-line no-console
  console.log('[HyperLocalReferralTracker] getAllReferrals result', {
    count: Array.isArray(data) ? data.length : 0,
    sample: data?.[0],
  });
  return data;
};

/**
 * Get a single influencer's details and referral history.
 * @param {string} influencerId
 * @returns {Promise<Object|null>}
 */
export const getInfluencerDetails = async (influencerId) => {
  console.log('[referralTrackerApi] getInfluencerDetails called', { influencerId });
  if (!influencerId) return null;

  if (USE_MOCK_REFERRALS) {
    await maybeDelay(200);
    return MOCK_REFERRALS.find((r) => r.influencer_id === influencerId) || null;
  }

  try {
    const [influencer, codes] = await Promise.all([
      marketingRequest('GET', `/marketing/influencers/${influencerId}`),
      fetchReferralCodes({ influencer_id: influencerId }),
    ]);

    const infCodes = extractList(codes, ['referral_codes', 'codes']);
    // Pass influencer_id to filter attributions correctly
    const attributions = (
      await Promise.all(
        infCodes.map((code) => fetchAttributionsForCode(code.referral_code || code.code, influencerId))
      )
    ).flat();

    const view = buildInfluencerView(
      influencer?.influencer || influencer,
      infCodes,
      attributions
    );

    const result = {
      ...view,
      referral_code: view.referral_code || infCodes[0]?.referral_code || infCodes[0]?.code,
      recent_referrals: attributions
        .slice(0, 10)
        .map((ref) => ({
          customer_id: ref.customer_id || ref.order_id || ref.id,
          referred_at: ref.referred_at || ref.created_at || ref.updated_at,
          converted: ref.converted ?? Boolean(ref.attributed_revenue),
        })),
    };
    console.log('[referralTrackerApi] getInfluencerDetails success', { influencerId });
    return result;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[HyperLocalReferralTracker] Failed to load influencer details', err);
    
    // Log error to logging service (errors from marketingRequest are already logged, but log here for completeness)
    loggingService.logError(
      'api_error_hyperlocal_referral',
      `Hyper Local Referral API error in getInfluencerDetails`,
      err,
      {
        endpoint: '/marketing/influencers',
        method: 'GET',
        influencerId,
        errorType: err?.name || 'Error',
      }
    ).catch(logError => {
      console.warn('[referralTrackerApi] Failed to log error:', logError);
    });
    
    return null;
  }
};

/**
 * Get aggregate stats for the referral tracker dashboard.
 * @returns {Promise<{ totalReferrals: number, activeInfluencers: number, totalConversions: number }>}
 */
export const getReferralStats = async () => {
  const referrals = await getAllReferrals();
  // eslint-disable-next-line no-console
  console.log('[HyperLocalReferralTracker] Computing stats from referrals', {
    count: referrals.length,
  });
  const activeInfluencers = referrals.filter((r) => (r.status || 'active') === 'active').length;
  const totalReferrals = referrals.reduce(
    (sum, r) => sum + (Number.isFinite(r.referral_count) ? r.referral_count : 0),
    0
  );
  // Conversion data is not exposed separately; use attribution count as a placeholder.
  const totalConversions = totalReferrals;

  const stats = {
    totalReferrals,
    activeInfluencers,
    totalConversions,
  };

  // eslint-disable-next-line no-console
  console.log('[HyperLocalReferralTracker] getReferralStats result', stats);

  return stats;
};

const VALID_STATUSES = ['active', 'inactive', 'archived'];
const SOCIAL_HANDLE_KEYS = ['instagram', 'youtube', 'facebook'];

/**
 * Sanitize and validate payload for POST /marketing/influencers.
 * Only includes fields that are valid; never sends undefined/null to backend.
 * @param {Object} payload - Raw form/input payload
 * @returns {{ body: Object, errors: string[] }}
 */
const validateAndSanitizeInfluencerPayload = (payload) => {
  const errors = [];
  const body = {};

  // Required: site_id
  const siteId =
    payload != null && payload.site_id != null ? String(payload.site_id).trim() : '';
  if (!siteId) {
    errors.push('site_id is required.');
    return { body: null, errors };
  }
  body.site_id = siteId;

  // Optional strings per API doc: name, email, phone (no location/region in Create body)
  const optionalStrings = [
    { key: 'name', value: payload?.name },
    { key: 'email', value: payload?.email },
    { key: 'phone', value: payload?.phone },
  ];
  for (const { key, value } of optionalStrings) {
    if (value != null && String(value).trim() !== '') {
      body[key] = String(value).trim();
    }
  }

  // Optional: social_handles - only include keys with non-empty string values
  if (payload?.social_handles != null && typeof payload.social_handles === 'object') {
    const sanitized = {};
    for (const k of SOCIAL_HANDLE_KEYS) {
      const v = payload.social_handles[k];
      if (v != null && String(v).trim() !== '') {
        sanitized[k] = String(v).trim();
      }
    }
    if (Object.keys(sanitized).length > 0) {
      body.social_handles = sanitized;
    }
  }

  // Optional: status - must be one of active | inactive | archived (default: "active")
  if (payload?.status != null && String(payload.status).trim() !== '') {
    const s = String(payload.status).trim().toLowerCase();
    if (VALID_STATUSES.includes(s)) {
      body.status = s;
    }
  }
  if (!body.status) {
    body.status = 'active';
  }

  return { body, errors };
};

/**
 * Create a new influencer.
 * POST /marketing/influencers
 * Request body (API doc v2.3): site_id (required), name, email, phone, social_handles { instagram, youtube }, status (active|inactive|archived).
 * @param {Object} payload
 * @param {string} payload.site_id - Required - Restaurant site identifier
 * @param {string} [payload.name]
 * @param {string} [payload.email]
 * @param {string} [payload.phone]
 * @param {Object} [payload.social_handles] - { instagram?, youtube?, facebook? }
 * @param {string} [payload.status] - "active" | "inactive" | "archived" (default: "active")
 * @returns {Promise<{ message: string, influencer_id: string }>}
 */
export const createInfluencer = async (payload) => {
  if (USE_MOCK_REFERRALS) {
    throw new Error('Mock mode enabled; live API calls are disabled.');
  }

  const { body, errors } = validateAndSanitizeInfluencerPayload(payload);
  if (errors.length > 0 || body == null) {
    throw new Error(errors[0] || 'site_id is required.');
  }

  const res = await marketingRequest('POST', '/marketing/influencers', { body });
  referralCache = null;
  referralCacheTimestamp = 0;
  return res;
};

/**
 * Update an influencer (partial update supported).
 * PUT /marketing/influencers/{influencer_id}
 * @param {string} influencerId - Influencer ID to update
 * @param {Object} payload - Fields to update (e.g. name, email, phone, status, social_handles, site_id)
 * @returns {Promise<{ message: string, influencer: Object }>} 200 OK with message and updated influencer
 */
export const updateInfluencer = async (influencerId, payload) => {
  console.log('[referralTrackerApi] updateInfluencer called', { influencerId, payload });
  if (!influencerId) {
    throw new Error('influencer_id is required.');
  }
  if (USE_MOCK_REFERRALS) {
    throw new Error('Mock mode enabled; live API calls are disabled.');
  }
  const body = payload && typeof payload === 'object' ? payload : {};
  const res = await marketingRequest('PUT', `/marketing/influencers/${influencerId}`, { body });
  console.log('[referralTrackerApi] updateInfluencer success', { influencerId, response: res });
  referralCache = null;
  referralCacheTimestamp = 0;
  return res;
};

/**
 * Delete an influencer.
 * DELETE /marketing/influencers/{influencer_id}
 * @param {string} influencerId - Influencer ID to delete
 * @returns {Promise<Object>} API response (e.g. { message } or empty body)
 */
export const deleteInfluencer = async (influencerId) => {
  console.log('[referralTrackerApi] deleteInfluencer called', { influencerId });
  if (!influencerId) {
    throw new Error('influencer_id is required.');
  }
  if (USE_MOCK_REFERRALS) {
    throw new Error('Mock mode enabled; live API calls are disabled.');
  }
  const res = await marketingRequest('DELETE', `/marketing/influencers/${influencerId}`);
  console.log('[referralTrackerApi] deleteInfluencer success', { influencerId, response: res });
  referralCache = null;
  referralCacheTimestamp = 0;
  return res;
};

/**
 * Get dashboard data: stats, top influencers, referrals by region (or site).
 * @returns {Promise<{
 *   stats: { totalReferrals, activeInfluencers, totalConversions, conversionRate },
 *   topInfluencers: Array<{ influencer_id, name, referral_count, location, region }>,
 *   referralsByRegion: Array<{ region: string, value: number }>
 * }>}
 */
export const getDashboardData = async () => {
  const referrals = await getAllReferrals();
  const stats = await getReferralStats();
  // eslint-disable-next-line no-console
  console.log('[HyperLocalReferralTracker] Raw data for dashboard', {
    referralsCount: referrals.length,
    stats,
  });

  const topInfluencers = [...referrals]
    .sort((a, b) => (b.referral_count || 0) - (a.referral_count || 0))
    .slice(0, 5)
    .map((r) => ({
      influencer_id: r.influencer_id,
      name: r.name,
      referral_count: r.referral_count || 0,
      location: r.location || r.site_name,
      region: r.region || r.site_name || r.site_id || 'Unknown',
    }));

  const regionMap = referrals.reduce((acc, r) => {
    const key = r.region || r.site_name || r.site_id || 'Other';
    acc[key] = (acc[key] || 0) + (r.referral_count || 0);
    return acc;
  }, {});

  const referralsByRegion = Object.entries(regionMap).map(([region, value]) => ({
    region,
    value,
  }));

  const conversionRate =
    stats.totalReferrals > 0 ? (stats.totalConversions / stats.totalReferrals) * 100 : 0;

  return {
    stats: { ...stats, conversionRate },
    topInfluencers,
    referralsByRegion,
  };
};
